import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import { ModalForm, DefaultInputPropTypes, TextInput, useInputHelper, FormFieldsData } from 'framework/form';
import { useProjectDetails } from 'domain/projects/newProject/dashboard/hooks/useProjectDashboard';
import { useEmployeeService } from 'domain/shared/employee';
import ReportWysiwygInput from './ReportWysiwygInput';
import useCustomerReportService from '../useCustomerReportService';
import { useBlobPreview, BlobPreviewModal } from 'framework/ui/Blob';
import { Stack, Typography, Box } from '@mui/material';
import { FILE_CATEGORY } from 'domain/shared/artifact';
import React, { useState, useCallback } from 'react';
import { Recipient, RecipientType } from 'domain/shared/newProject';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

const FileInput = (props: DefaultInputPropTypes<string | string[]>) => {
  const { getValue } = useInputHelper(props);

  return (
    <Stack direction="row" spacing={1} alignItems="center" marginBottom={2}>
      <span>
        <img src={FILE_CATEGORY.PDF.icon} alt="" height={48} width={48} />
      </span>
      <Typography>{getValue()}</Typography>
    </Stack>
  );
};

const SendReportModal = ({ projectId, contentGenerator, titleInterval, disabled, onSubmit }: any) => {
  const { recipientsSearch, getSendReportValidationSchema } = useCustomerReportService();
  const { project } = useProjectDetails(projectId!);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [modalAfterSendOpen, setModalAfterSendOpen] = useState(false);
  const { getById } = useEmployeeService();
  const { getUserAlias } = useAuthenticatedUserService();

  const { blobUrl, loading, setLoading, previewOpen, setPreviewOpen, closePreview, createBlobUrlFromBase64 } =
    useBlobPreview();

  const [cachedContent, setCachedContent] = useState<string | null>(null);

  const generatePdfBlob = useCallback(async () => {
    setLoading(true);
    try {
      const content = await contentGenerator();
      if (content) {
        createBlobUrlFromBase64(content);
        setCachedContent(content);
      }
    } finally {
      setLoading(false);
    }
  }, [contentGenerator, setLoading, createBlobUrlFromBase64]);

  const getDataFunc = async () => {
    const currentUserAlias = await getUserAlias();

    return Promise.all([
      recipientsSearch(projectId!, {
        filter: {
          deletedAt: {
            null: true,
          },
        },
      }),
      currentUserAlias ? getById(currentUserAlias) : Promise.resolve(null),
      cachedContent ? Promise.resolve(cachedContent) : contentGenerator(),
    ]).then(([data, employeeData, content]) => {
      const toRecipients =
        data.result.filter((r: any) => r.type === RecipientType.MAIN).map((v: Recipient) => v.email) || [];
      const ccRecipients =
        data.result.filter((r: any) => r.type === RecipientType.CC).map((v: Recipient) => v.email) || [];
      const bccRecipients = employeeData?.workingEmail ? [employeeData.workingEmail] : [];

      if (content && !cachedContent) {
        createBlobUrlFromBase64(content);
        setCachedContent(content);
      }

      return {
        toRecipients,
        ccRecipients,
        bccRecipients,
        emailSubject: `Project ${project?.name}: status report for${titleInterval}`,
        emailBody: '',
        fileName: `Status report for ${titleInterval}.pdf`,
        content: content || cachedContent,
      };
    });
  };

  const openPreview = useCallback(() => {
    setPreviewOpen(true);
    if (!blobUrl) generatePdfBlob();
  }, [blobUrl, generatePdfBlob, setPreviewOpen]);

  const openFormModal = useCallback(() => {
    closePreview();
    setFormModalOpen(true);
  }, [closePreview]);

  const submitForm = async (formData: FormFieldsData) => {
    return onSubmit(formData);
  };

  const fileName = `Status report for ${titleInterval}.pdf`;

  return (
    <>
      <Button variant="contained" disabled={disabled} onClick={openPreview}>
        Send
      </Button>
      <Modal
        isOpen={formModalOpen}
        onToggle={() => setFormModalOpen(false)}
        paperStyles={{ backgroundColor: 'var(--mui-palette-background-paper)' }}
      >
        <ModalForm
          getDataFunc={getDataFunc}
          closeModal={() => setFormModalOpen(false)}
          modalOpen={formModalOpen}
          getValidationSchemaFunc={getSendReportValidationSchema}
          submitFormFunc={submitForm}
          afterSubmit={{
            execute: () => setModalAfterSendOpen(true),
            message: 'You are awesome! Report was sent.',
          }}
        >
          <TextInput source="toRecipients" label="To:" disabled />
          <TextInput source="ccRecipients" label="CC:" disabled />
          <TextInput source="bccRecipients" label="BCC:" disabled />
          <TextInput source="emailSubject" label="Subject:" />
          <FileInput source="fileName" />
          <ReportWysiwygInput source="emailBody" label="" placeholder="Please, provide message here" />
        </ModalForm>
      </Modal>
      <Modal isOpen={modalAfterSendOpen} onToggle={() => setModalAfterSendOpen(!modalAfterSendOpen)} hasCloseIcon>
        <Box textAlign="center" display="flex" justifyContent="center" pb="40px">
          <Typography variant="h6">
            {`The customer report for ${titleInterval} was successfully sent to the client.`}
          </Typography>
        </Box>
      </Modal>
      <BlobPreviewModal
        open={previewOpen}
        blobUrl={blobUrl}
        fileName={fileName}
        onClose={closePreview}
        loading={loading}
        approvalFooter={{
          onNext: openFormModal,
          onCancel: closePreview,
          sendButtonText: 'Next',
          approvalLabel: 'I have reviewed the document and approve its content for sending',
        }}
      />
    </>
  );
};
export default SendReportModal;
